import React, {useEffect} from 'react';
import {Box, Grid, Typography} from '@mui/material';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import type {newPlanFields} from './builderSetUpNewFields';
import {
  BuilderColumnWrapper,
  BuilderSourceColumnHeader,
} from '../CreateBuilder/BuilderSourceDestination/BuilderSourceDestination';
import {BuilderProps} from '../CreateBuilder/BuilderSourceDestination/builderValidators';
import {SetUpNewParent} from './SetUpNewParent';
import {BuilderWrappedNewSubPlans} from './BuilderListNewSubPlans';
import {hasWrapper} from '../CreateBuilder/BuilderSourceDestination/builderUtils';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {useResetBuilder} from '../CreateBuilder/BuilderSourceDestination/useResetBuilder';
import {useAccountContext} from '../../../contexts/AccountContext';
import usePlanNamesProviders from '../../../hooks/usePlanNamesProviders';
import validateUUID from '../../../utils/validateUUID';
import {sanitiseProductType} from '../../../utils/sanitiseProductType';

import {useAutoForm, useAutoField} from '../../../components/AutoForm/useAutoForm';
import {
  getSetUpNewInitialValues,
  useDeriveIsLowesPension,
  useDeriveIsTransactBond,
} from '../hooks/builderSetUpNewHooks';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {UPDATES} from '../../../shared/UPDATES';

export const BuilderSetUpNew = ({contactName, sourceDestinationLabels}: BuilderProps) => {
  useResetBuilder(OVRecommendActionEnum.SET_UP_NEW);
  const [newPlanParentField] = useAutoField<newPlanFields['parent']>('setUpNew.parent');
  const {builderDetails} = useBuilderContext();
  const {values, setFieldValue} = useAutoForm<builderFieldsType>();
  const {accountId} = useAccountContext();
  const {
    productProviders,
    productNames,
    productProvidersOptionGroups,
    queryGetProductProviders,
    queryGetProducts,
  } = usePlanNamesProviders();
  const isLowesPension = useDeriveIsLowesPension(values.mainPlanType);
  const isTransactBond = useDeriveIsTransactBond(values.mainPlanType);

  useEffect(() => {
    if (!builderDetails) {
      setFieldValue(UPDATES._D678, 'setUpNew', {
        parent: {
          planProvider: '',
          productNameId: '',
          contactId: '',
        },
        subplans: [
          {
            existingPotId: null,
          },
        ],
      });
    }
  }, [builderDetails]);

  useEffect(() => {
    if (values.mainPlanType) {
      queryGetProductProviders({
        variables: {
          accountId: validateUUID(accountId),
          productType: sanitiseProductType(values.mainPlanType),
          productBasis: 'accumulation',
        },
      });
    }
  }, [values.mainPlanType]);

  useEffect(() => {
    if (newPlanParentField.value?.planProvider) {
      queryGetProducts({
        variables: {
          productType: sanitiseProductType(values.mainPlanType),
          providerId: newPlanParentField.value.planProvider,
        },
      });
    }
  }, [
    newPlanParentField.value?.planProvider,
    values.mainPlanType,
    newPlanParentField.value?.productNameId,
  ]);

  if (!values.mainPlanType) {
    return <Typography>Please choose a plan type above</Typography>;
  }

  return (
    <BuilderColumnWrapper>
      <Grid item xs={12} data-test="Source Choices">
        <BuilderSourceColumnHeader sourceLabel={`New ${values.mainPlanType}`} />
        <SetUpNewParent
          contactName={contactName}
          productProvidersOptionGroups={productProvidersOptionGroups}
          productProviders={productProviders}
          productNames={productNames}
        />
        {!isLowesPension && !isTransactBond && (
          <Box className="subplans" mt={2}>
            <BuilderWrappedNewSubPlans productProviders={productProviders} />
          </Box>
        )}
      </Grid>
    </BuilderColumnWrapper>
  );
};
